import React from 'react';
import PropTypes from 'prop-types';
import path from 'path-browserify';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { getFileTypeIconProps } from '@uifabric/file-type-icons';
import { TooltipHost } from 'office-ui-fabric-react';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import Module from '../Module';
import ModuleContent from '../ModuleContent';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import DetailsList from '../DetailsList';
import TextButton from '../TextButton';
import DetailsListOverflowTooltip from '../DetailsListOverflowTooltip';
import { formatDateString, pxToRem } from '../../utils';
import styles from './PendingRequestApprovals.style';

const PendingRequestApprovals = ({ pendingAttachments, onDownload, onRemove, ...other }) => {
  const columns = {
    fileIcon: {
      key: 'fileIcon',
      name: 'File Type',
      isIconOnly: true,
      isHeaderVisible: false,
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      columnActionsMode: 0,
      minWidth: 16,
      maxWidth: 16,
      onRender(attachment) {
        const extension = path.extname(attachment.displayName);
        return (
          <FontIcon {...getFileTypeIconProps({ extension, size: 16, imageFileType: 'png' })} />
        );
      },
    },
    owner: {
      key: 'createdBy',
      name: 'Owner',
      fieldName: 'owner',
      columnActionsMode: 0,
      minWidth: 120,
      maxWidth: 120,
      isPadded: true,
      onRender: item => (
        <div style={{ width: '100%' }}>
          <DetailsListOverflowTooltip
            ariaLabel="owner"
            displayContent={item.createdBy?.displayName || item.createdBy?.oid}
            tooltipContent={item.createdBy?.displayName || item.createdBy?.oid}
          />
          <br /> {formatDateString(item.createdTimestamp)}
        </div>
      ),
    },
    fileName: {
      key: 'name',
      name: 'File Name',
      fieldName: 'displayName',
      minWidth: 120,
      maxWidth: 120,
      columnActionsMode: 0,
      isPadded: true,
      onRender: item => (
        <DetailsListOverflowTooltip
          ariaLabel="File name"
          displayContent={item.displayName}
          tooltipContent={item.displayName}
        />
      ),
    },
    approver: {
      key: 'reviewer',
      name: 'Approver',
      fieldName: 'reviewer',
      columnActionsMode: 0,
      minWidth: 120,
      maxWidth: 120,
      isPadded: true,
      onRender: item => (
        <DetailsListOverflowTooltip
          ariaLabel="approver"
          displayContent={item.reviewer?.displayName || item.reviewer}
          tooltipContent={item.reviewer?.displayName || item.reviewer}
        />
      ),
    },
    lastModified: {
      key: 'lastUpdatedTimestamp',
      name: 'Last Modified',
      fieldName: 'lastUpdatedTimestamp',
      columnActionsMode: 0,
      minWidth: 50,
      maxWidth: 50,
      isPadded: true,
      onRender: item => (
        <DetailsListOverflowTooltip
          ariaLabel="last modified"
          displayContent={formatDateString(item.lastUpdatedTimestamp)}
        />
      ),
    },
    commentIcon: {
      key: 'comment',
      name: 'Comment',
      fieldName: 'displayName',
      minWidth: 30,
      maxWidth: 30,
      columnActionsMode: 0,
      isPadded: true,
      className: styles.alignCenter,
      onRender(attachment) {
        if (attachment.comment) {
          return (
            <TooltipHost
              content={attachment.comment}
              calloutProps={{
                gapSpace: 0,
                target: `#select-message-${attachment.attachmentId}`,
              }}
              styles={{
                root: {
                  display: 'flex',
                },
              }}
            >
              <IconButton
                iconProps={{ iconName: 'CannedChat', style: { fontSize: pxToRem(14) } }}
                ariaLabel="Comment"
                id={`select-message-${attachment.attachmentId}`}
                className={styles.infoIcon}
              />
            </TooltipHost>
          );
        }

        return '';
      },
    },
    remove: {
      key: 'remove',
      name: 'Remove',
      arianame: 'remove',
      isIconOnly: true,
      fieldName: 'remove',
      columnActionsMode: 0,
      minWidth: 50,
      maxWidth: 50,
      onRender: item => {
        return (
          <TextButton
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onRemove(item.attachmentId);
            }}
          >
            Remove
          </TextButton>
        );
      },
    },
    download: {
      key: 'download',
      name: 'Download',
      arianame: 'download',
      isIconOnly: true,
      columnActionsMode: 0,
      fieldName: 'download',
      minWidth: 50,
      maxWidth: 50,
      onRender: item => {
        return (
          <TextButton
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onDownload(item);
            }}
          >
            Download
          </TextButton>
        );
      },
    },
  };

  const pendingViewColumns = [
    columns.fileIcon,
    columns.fileName,
    columns.approver,
    columns.lastModified,
    columns.owner,
    columns.commentIcon,
    columns.remove,
    columns.download,
  ];

  return (
    <Module>
      <ModuleContent>
        <SubModule closed>
          <SubModuleHeader id="openRequests" data-testid="open-requests" aria-label="open Requests">
            All Open Requests
          </SubModuleHeader>
          <SubModuleContent>
            {pendingAttachments && pendingAttachments.length > 0 && (
              <DetailsList
                columns={pendingViewColumns}
                items={pendingAttachments}
                multiLine
                {...other}
              />
            )}
          </SubModuleContent>
        </SubModule>
      </ModuleContent>
    </Module>
  );
};

PendingRequestApprovals.propTypes = {
  pendingAttachments: PropTypes.array,
  onDownload: PropTypes.func,
  onRemove: PropTypes.func,
};

PendingRequestApprovals.defaultProps = {
  pendingAttachments: [],
  onDownload: () => {},
  onRemove: () => {},
};

export default PendingRequestApprovals;
